import { SP } from "./namespace";
import {
  Particle,
  SPELLS,
  TAU,
  clamp,
  lerp,
  rand,
  rgba,
} from "../client";

/** Blue raises reflect; green's fireball bounces off and hits green. */
SP.drawReflect = function(sim, cfg, t, lt) {
  const c = sim.ctx;
  const A = sim.anim || (sim.anim = {});
  const blueX = cfg.casterX;
  const greenX = cfg.targetX;
  const y = cfg.y;
  const fb = SPELLS.fireball;
  const fbCol = fb ? fb.color : "#ff7a2f";
  const fbPr = ((fb && fb.radius) || 13) * 0.62;

  const shieldStart = 0.3;
  const shootStart = 0.58;
  const inboundStart = 0.7;
  const bounceAt = 1.12;
  const hitGreenAt = 1.58;

  const zones = [];
  const projs = [];
  let targetKnock = 0;
  let targetFlash = 0;
  let greenGlow = 0;

  if (lt >= shieldStart) {
    zones.push({
      x: blueX, y,
      r: cfg.playerR + 9 + Math.sin(t * 6) * 2,
      color: "#9fe7ff",
      alpha: 0.72,
      phase: t,
    });
  }
  if (lt >= shootStart && lt < bounceAt) {
    greenGlow = clamp((lt - shootStart) / 0.14, 0, 1) * 0.9;
  }

  if (lt >= inboundStart && lt < hitGreenAt + 0.3) {
    let px;
    let ang;
    if (lt < bounceAt) {
      const u = (lt - inboundStart) / (bounceAt - inboundStart);
      px = lerp(greenX - 24, blueX + cfg.playerR + 6, u);
      ang = Math.PI;
      if (Math.random() < 0.38) {
        sim.particles.push(new Particle(px, y + rand(-3, 3), rand(-45, -15), rand(-10, 10),
          rand(0.08, 0.22), fbCol, rand(2, 4), { kind: "glow" }));
      }
    } else {
      if (!A.bounced) {
        A.bounced = true;
        const bx = blueX + cfg.playerR + 4;
        SP.spawnHit(sim, bx, y, "#9fe7ff", "reflect");
      }
      const u = clamp((lt - bounceAt) / (hitGreenAt - bounceAt), 0, 1);
      px = lerp(blueX + cfg.playerR + 6, greenX - 12, u);
      ang = 0;
      if (lt >= hitGreenAt) {
        const hk = clamp((lt - hitGreenAt) / 0.32, 0, 1);
        targetKnock = Math.sin(hk * Math.PI) * 16;
        targetFlash = clamp(1 - (lt - hitGreenAt) / 0.22, 0, 1);
        if (!A.hitGreen) {
          A.hitGreen = true;
          SP.spawnHit(sim, greenX, y, fbCol, "fireball");
        }
      }
    }
    projs.push({ x: px, y, ang, projSpell: "fireball", col: fbCol, pr: fbPr });
  }

  c.clearRect(0, 0, cfg.w, cfg.h);
  const bg = c.createRadialGradient(cfg.cx, cfg.cy, 10, cfg.cx, cfg.cy, cfg.w * 0.72);
  bg.addColorStop(0, "#252040");
  bg.addColorStop(1, "#0e0c1c");
  c.fillStyle = bg;
  c.fillRect(0, 0, cfg.w, cfg.h);
  SP.drawPlatform(c, cfg.cx, cfg.cy, cfg.platformR, t);
  for (const z of zones) SP.drawZone(c, z, t);
  SP.drawShockwaves(c, sim.shockwaves);
  const bob = Math.sin(t * 2.6) * 1.1;
  SP.drawWizard(c, blueX, y, cfg.casterRobe, 1, bob, cfg.playerR, lt < shootStart ? 0.35 : 0.15);
  const gx = greenX + targetKnock;
  SP.drawWizard(c, gx, y, cfg.targetRobe, -1, bob * 0.9, cfg.playerR, greenGlow);
  if (targetFlash > 0) {
    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = targetFlash * 0.55;
    c.fillStyle = fbCol;
    c.beginPath(); c.arc(gx, y, cfg.playerR * 1.5, 0, TAU); c.fill();
    c.restore();
  }
  SP.drawParticles(c, sim.particles);
  for (const p of projs) {
    c.save();
    c.translate(p.x, p.y);
    SP.drawProj(c, p.projSpell || "fireball", p.pr || fbPr, p.col || fbCol, p.ang || 0, t);
    c.restore();
  }
};

/** Void rip at arena center — pulls blue and green inward; both take core damage. */
SP.drawVoidRip = function(sim, cfg, t, lt) {
  const c = sim.ctx;
  const A = sim.anim || (sim.anim = {});
  const s = SPELLS.voidrip;
  const col = s ? s.color : "#2a1840";
  const blueX0 = cfg.casterX;
  const greenX0 = cfg.targetX;
  const midX = cfg.cx;
  const y = cfg.y;
  const arm = (s && s.armDelay) || 0.55;
  const zoneStart = 0.48;
  const active = zoneStart + arm;
  const pullEnd = active + 0.85;
  const dmgStart = active + 0.35;

  const zones = [];
  let blueX = blueX0;
  let greenX = greenX0;
  let blueFlash = 0;
  let greenFlash = 0;
  let centerGlow = 0;

  if (lt >= zoneStart - 0.12 && lt < zoneStart) {
    centerGlow = clamp((lt - (zoneStart - 0.12)) / 0.12, 0, 1) * 0.75;
  }

  if (lt >= zoneStart) {
    const armed = lt >= active;
    const grow = armed ? clamp((lt - active) / 0.5, 0, 1) : clamp((lt - zoneStart) / arm, 0, 1);
    const outerR = 18 + grow * 22;
    zones.push({
      x: midX,
      y,
      r: outerR,
      color: col,
      fill: rgba(col, armed ? 0.2 : 0.1),
      core: "#1a1028",
      alpha: armed ? 0.88 : 0.45 + 0.25 * Math.sin(t * 10),
      phase: 3,
      lineW: armed ? 2.5 : 2,
    });

    if (lt >= active) {
      const u = clamp((lt - active) / (pullEnd - active), 0, 1);
      const pull = u < 0.72 ? (u / 0.72) : 1;
      blueX = lerp(blueX0, midX - cfg.playerR - 3, pull);
      greenX = lerp(greenX0, midX + cfg.playerR + 3, pull);

      if (lt >= dmgStart) {
        const dmg = clamp((pull - 0.35) / 0.55, 0, 1);
        const flicker = 0.5 + 0.5 * Math.sin(t * 14);
        blueFlash = dmg * 0.6 * flicker;
        greenFlash = dmg * 0.6 * flicker;
        if (pull > 0.5 && !A.voidPop) {
          A.voidPop = true;
          SP.spawnHit(sim, midX, y, col, "voidrip");
        }
        if (Math.random() < 0.32) {
          const fromBlue = Math.random() < 0.5;
          const sx = fromBlue ? blueX : greenX;
          const dir = fromBlue ? 1 : -1;
          sim.particles.push(new Particle(sx, y + rand(-6, 6), dir * rand(25, 55), rand(-12, 12),
            rand(0.12, 0.28), "#6b4a9a", rand(2, 4), { kind: "glow", grav: 8 }));
        }
      }
    }
  }

  c.clearRect(0, 0, cfg.w, cfg.h);
  const bg = c.createRadialGradient(cfg.cx, cfg.cy, 10, cfg.cx, cfg.cy, cfg.w * 0.72);
  bg.addColorStop(0, "#252040");
  bg.addColorStop(1, "#0e0c1c");
  c.fillStyle = bg;
  c.fillRect(0, 0, cfg.w, cfg.h);
  SP.drawPlatform(c, cfg.cx, cfg.cy, cfg.platformR, t);
  for (const z of zones) SP.drawZone(c, z, t);
  if (centerGlow > 0 && lt < active) {
    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = centerGlow * 0.5;
    c.fillStyle = col;
    c.beginPath(); c.arc(midX, y, 10 + centerGlow * 6, 0, TAU); c.fill();
    c.restore();
  }
  SP.drawShockwaves(c, sim.shockwaves);
  const bob = Math.sin(t * 2.6) * 1.1;
  SP.drawWizard(c, blueX, y, cfg.casterRobe, 1, bob, cfg.playerR, blueFlash * 0.25);
  SP.drawWizard(c, greenX, y, cfg.targetRobe, -1, bob * 0.9, cfg.playerR, greenFlash * 0.25);
  const flashCol = col;
  for (const [fx, flash] of [[blueX, blueFlash], [greenX, greenFlash]]) {
    if (flash <= 0) continue;
    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = flash * 0.55;
    c.fillStyle = flashCol;
    c.beginPath(); c.arc(fx, y, cfg.playerR * 1.45, 0, TAU); c.fill();
    c.restore();
  }
  SP.drawParticles(c, sim.particles);
};

SP.draw = function(sim, t, dt) {
  const cfg = sim.scene;
  const spellId = cfg.spellId;
  const s = SPELLS[spellId];
  if (!s) return;
  const c = sim.ctx;
  const lt = SP.loopStep(sim, cfg, t, dt);
  if (spellId === "reflect") {
    SP.drawReflect(sim, cfg, t, lt);
    return;
  }
  if (spellId === "voidrip") {
    SP.drawVoidRip(sim, cfg, t, lt);
    return;
  }
  const A = sim.anim || (sim.anim = {});
  const col = s.color;
  const pr = (s.radius || 10) * 0.62;
  let castGlow = 0, targetKnock = 0, targetFlash = 0;
  let casterX = cfg.casterX, targetX = cfg.targetX;
  const projs = [];
  const zones = [];
  const beams = [];
  let wall = null;
  const flyStart = s.windup ? 0.48 + s.windup * 0.38 : cfg.flyStart;
  const speed = SP.flySpeed(spellId, s);
  const hitAt = flyStart + (cfg.hitX - cfg.spawnX) / speed;

  const phaseHit = () => {
    if (!sim.hitSpawned) {
      sim.hitSpawned = true;
      SP.spawnHit(sim, cfg.hitX, cfg.y, col, spellId);
    }
    const hk = clamp((lt - hitAt) / 0.4, 0, 1);
    targetKnock = Math.sin(hk * Math.PI) * 15;
    targetFlash = clamp(1 - (lt - hitAt) / 0.2, 0, 1);
  };

  if (lt >= cfg.castAt && lt < flyStart) {
    castGlow = clamp((lt - cfg.castAt) / (flyStart - cfg.castAt), 0, 1);
  }

  switch (s.type) {
    case "burst": {
      const wind = s.windup || 0;
      const boomAt = 0.48 + wind * 0.95;
      if (lt >= cfg.castAt && lt < boomAt) castGlow = clamp((lt - cfg.castAt) / (boomAt - cfg.castAt), 0, 1);
      if (spellId === "frostnova") {
        if (lt >= flyStart && lt < flyStart + 0.55) {
          const p = (lt - flyStart) / 0.55;
          zones.push({ x: casterX + 8, y: cfg.y, r: 12 + p * 48, color: col, alpha: 1 - p * 0.5, phase: 0 });
        }
        if (lt >= flyStart + 0.08 && !sim.hitSpawned) {
          sim.hitSpawned = true;
          SP.spawnHit(sim, cfg.hitX, cfg.y, col, spellId);
        }
      } else if (lt >= boomAt) {
        const p = clamp((lt - boomAt) / 0.35, 0, 1);
        zones.push({ x: casterX + 10, y: cfg.y, r: 8 + p * 55, color: col, alpha: 0.9, phase: 1 });
        if (p > 0.2) phaseHit();
        if (p > 0.15) casterX -= Math.sin(p * Math.PI) * 6;
      }
      break;
    }
    case "hitscan": {
      if (lt >= flyStart + 0.05) {
        beams.push({ x0: cfg.spawnX, y0: cfg.y - 4, x1: cfg.hitX, y1: cfg.y, color: col, width: 2.5, t: 0, life: 0.2 });
        if (lt < flyStart + 0.25) phaseHit();
      }
      break;
    }
    case "chargebeam": {
      const chEnd = flyStart + 0.75;
      if (lt >= cfg.castAt && lt < chEnd) castGlow = clamp((lt - cfg.castAt) / (chEnd - cfg.castAt), 0, 1);
      if (lt >= chEnd && lt < chEnd + 0.35) {
        beams.push({ x0: cfg.spawnX, y0: cfg.y, x1: cfg.hitX + 20, y1: cfg.y, color: col, width: 5, t: 0, life: 0.35 });
        if (lt < chEnd + 0.2) phaseHit();
        casterX -= 8 * clamp((lt - chEnd) / 0.15, 0, 1);
      }
      break;
    }
    case "chain": {
      if (lt >= flyStart) {
        const mid = [cfg.casterX + (cfg.hitX - cfg.casterX) * 0.55, cfg.y - 18];
        beams.push({ x0: cfg.spawnX, y0: cfg.y, mid, x1: cfg.hitX, y1: cfg.y, color: col, width: 2.5, t: 0, life: 0.25 });
        if (lt < flyStart + 0.22) phaseHit();
      }
      break;
    }
    case "blizzard": {
      const zoneAt = flyStart;
      if (lt >= zoneAt) {
        const dur = 1.4;
        const p = clamp((lt - zoneAt) / dur, 0, 1);
        zones.push({ x: cfg.hitX, y: cfg.y, r: 28 + p * 8, color: col, alpha: 0.75, phase: 2 });
        if (Math.random() < 0.4) {
          sim.particles.push(new Particle(cfg.hitX + rand(-24, 24), cfg.y - 40 + rand(-8, 8),
            rand(-10, 10), rand(60, 120), rand(0.2, 0.45), "#d6f6ff", rand(2, 4), { kind: "glow", grav: 40 }));
        }
        if (lt > zoneAt + 0.2 && lt < zoneAt + 0.5) targetFlash = 0.4;
      }
      break;
    }
    case "illusion":
      break;
    case "dash": {
      if (lt >= flyStart && lt < hitAt + 0.15) {
        const p = clamp((lt - flyStart) / (hitAt - flyStart), 0, 1);
        casterX = lerp(cfg.casterX, cfg.hitX - 28, p);
        if (p > 0.85) phaseHit();
      }
      break;
    }
    case "snare": {
      const arm = s.armDelay || 0.4;
      const start = flyStart;
      const active = start + arm;
      if (lt >= start) {
        const p = clamp((lt - active) / 1.2, 0, 1);
        const r = (s.radius || 80) * 0.22 + p * 6;
        zones.push({
          x: cfg.hitX, y: cfg.y, r,
          color: col,
          fill: rgba(col, 0.15),
          alpha: lt < active ? 0.4 + 0.3 * Math.sin(t * 10) : 0.85,
          phase: 3,
        });
        if (lt >= active + 0.1) {
          targetX = lerp(cfg.targetX, cfg.hitX + 8, Math.sin(clamp((lt - active) / 0.8, 0, 1) * Math.PI) * 0.35);
          if (lt < active + 0.35) targetFlash = 0.35;
        }
      }
      break;
    }
    case "firewall": {
      if (lt >= flyStart) {
        wall = { x: cfg.hitX - 8, y: cfg.y, thick: 10, color: col, alpha: clamp((lt - flyStart) / 0.2, 0, 1) };
        if (lt > flyStart + 0.12 && lt < flyStart + 0.45) targetFlash = 0.45;
      }
      break;
    }
    case "self": {
      if (spellId === "teleport" && lt >= flyStart) {
        const p = clamp((lt - flyStart) / 0.28, 0, 1);
        if (p < 0.45) casterX = lerp(cfg.casterX, cfg.casterX, 1 - p * 2);
        else casterX = lerp(cfg.casterX, cfg.hitX - 36, (p - 0.45) / 0.55);
        castGlow = Math.sin(p * Math.PI) * 0.9;
      } else if (spellId === "windwalk" && lt >= flyStart) {
        const p = clamp((lt - flyStart) / (hitAt - flyStart + 0.1), 0, 1);
        casterX = lerp(cfg.casterX, cfg.hitX - 24, p);
        A.invis = 0.35 + 0.35 * Math.sin(t * 6);
        if (p > 0.88) phaseHit();
      } else if (spellId === "timeshift" && lt >= flyStart) {
        const p = clamp((lt - flyStart) / 0.9, 0, 1);
        if (p < 0.5) A.ghostX = lerp(cfg.casterX, cfg.casterX - 22, p * 2);
        else { casterX = lerp(cfg.casterX - 22, cfg.casterX, (p - 0.5) * 2); A.ghostX = null; }
        zones.push({ x: casterX, y: cfg.y, r: cfg.playerR + 10, color: col, alpha: 0.6, phase: 0 });
      } else if (lt >= flyStart) {
        const aura = spellId === "shield" ? "#7db4ff"
          : spellId === "barrier" ? "#c9a0ff"
          : spellId === "rush" ? "#9affb0" : col;
        zones.push({ x: casterX, y: cfg.y, r: cfg.playerR + 8 + Math.sin(t * 5) * 2, color: aura, alpha: 0.65, phase: t });
        castGlow = 0.5;
      }
      break;
    }
    default: {
      if (s.fall) {
        const dropAt = flyStart + 0.35;
        if (lt >= flyStart && lt < dropAt) {
          zones.push({ x: cfg.hitX, y: cfg.y, r: 14, color: col, alpha: 0.5 + 0.3 * Math.sin(t * 12), phase: 0 });
        } else if (lt >= dropAt && lt < hitAt + 0.15) {
          const p = (lt - dropAt) / (hitAt - dropAt);
          projs.push({ x: cfg.hitX, y: -20 + p * (cfg.y + 24), ang: Math.PI / 2 });
        } else if (lt >= hitAt) phaseHit();
      } else if (s.count && s.count > 1) {
        if (lt >= flyStart && lt < hitAt + 0.1) {
          const spread = s.spread || 0.45;
          for (let i = 0; i < s.count; i++) {
            const off = (i - (s.count - 1) / 2) * spread * 0.35;
            const prog = clamp((lt - flyStart - i * 0.04) / (hitAt - flyStart), 0, 1);
            if (prog <= 0) continue;
            projs.push({
              x: lerp(cfg.spawnX, cfg.hitX, prog),
              y: cfg.y + off * 28 + Math.sin(prog * Math.PI) * off * 12,
              ang: Math.atan2(off * 12, 1),
            });
          }
          if (lt >= hitAt) phaseHit();
        }
      } else if (s.boomerang) {
        if (lt >= flyStart && lt < hitAt + 0.5) {
          const total = hitAt - flyStart + 0.5;
          const u = (lt - flyStart) / total;
          const px = u < 0.55
            ? lerp(cfg.spawnX, cfg.hitX, u / 0.55)
            : lerp(cfg.hitX, cfg.spawnX + 20, (u - 0.55) / 0.45);
          projs.push({ x: px, y: cfg.y + Math.sin(u * Math.PI) * -8, ang: u < 0.55 ? 0 : Math.PI });
          if (u > 0.48 && u < 0.58) phaseHit();
        }
      } else if (s.homing) {
        if (lt >= flyStart && lt < hitAt + 0.05) {
          const prog = (lt - flyStart) / (hitAt - flyStart);
          const px = lerp(cfg.spawnX, cfg.hitX, prog);
          const py = cfg.y + Math.sin(prog * Math.PI) * -16;
          projs.push({ x: px, y: py, ang: Math.atan2(py - cfg.y, 2) });
          if (Math.random() < 0.45) {
            sim.particles.push(new Particle(px - 6, py, rand(-12, 8), rand(-10, 10),
              rand(0.1, 0.25), col, rand(2, 4), { kind: "glow" }));
          }
          if (lt >= hitAt) phaseHit();
        }
      } else if (s.bounce) {
        if (lt >= flyStart && lt < hitAt + 0.35) {
          const u = (lt - flyStart) / (hitAt - flyStart + 0.2);
          let px, py, ang = 0;
          if (u < 0.45) {
            px = lerp(cfg.spawnX, cfg.hitX - 20, u / 0.45);
            py = cfg.y;
          } else if (u < 0.7) {
            px = lerp(cfg.hitX - 20, cfg.hitX, (u - 0.45) / 0.25);
            py = lerp(cfg.y, cfg.y - 36, (u - 0.45) / 0.25);
            ang = -Math.PI / 2;
          } else {
            px = lerp(cfg.hitX, cfg.hitX - 10, (u - 0.7) / 0.3);
            py = lerp(cfg.y - 36, cfg.y, (u - 0.7) / 0.3);
            ang = Math.PI;
          }
          projs.push({ x: px, y: py, ang });
          if (u > 0.42 && u < 0.52) phaseHit();
        }
      } else if (spellId === "swap" && lt >= flyStart) {
        if (lt < hitAt) {
          const prog = (lt - flyStart) / (hitAt - flyStart);
          projs.push({ x: lerp(cfg.spawnX, cfg.hitX, prog), y: cfg.y, ang: 0 });
        } else if (lt < hitAt + 0.35) {
          const sw = clamp((lt - hitAt) / 0.25, 0, 1);
          casterX = lerp(cfg.casterX, cfg.targetX, sw);
          targetX = lerp(cfg.targetX, cfg.casterX, sw);
          if (!sim.hitSpawned) { sim.hitSpawned = true; SP.spawnHit(sim, cfg.hitX, cfg.y, col, spellId); }
        }
      } else if (spellId === "frozenorb") {
        if (lt >= flyStart && lt < hitAt + 0.25) {
          const prog = (lt - flyStart) / (hitAt - flyStart);
          const px = lerp(cfg.spawnX, cfg.hitX - 10, prog);
          projs.push({ x: px, y: cfg.y, ang: 0, big: true });
          if (Math.random() < 0.35) {
            const a = rand(0, TAU);
            sim.particles.push(new Particle(px, cfg.y, Math.cos(a) * 70, Math.sin(a) * 70,
              rand(0.15, 0.3), "#aef0ff", rand(2, 4), { kind: "glow" }));
          }
          if (lt >= hitAt) phaseHit();
        }
      } else if (lt >= flyStart && lt < hitAt) {
        const prog = (lt - flyStart) / (hitAt - flyStart);
        const px = lerp(cfg.spawnX, cfg.hitX, prog);
        projs.push({ x: px, y: cfg.y, ang: 0 });
        if (Math.random() < 0.4) {
          sim.particles.push(new Particle(px - 6, cfg.y + rand(-2, 2), rand(-14, 8), rand(-10, 10),
            rand(0.1, 0.28), col, rand(2, 5), { kind: "glow", grav: -35 }));
        }
      } else if (lt >= hitAt) {
        phaseHit();
        if (spellId === "splitter" && !A.split) {
          A.split = true;
          for (let i = 0; i < 6; i++) {
            const a = i / 6 * TAU;
            sim.particles.push(new Particle(cfg.hitX, cfg.y, Math.cos(a) * 90, Math.sin(a) * 90,
              rand(0.2, 0.45), col, rand(2, 5), { kind: "glow" }));
          }
        }
      }
      break;
    }
  }

  c.clearRect(0, 0, cfg.w, cfg.h);
  const bg = c.createRadialGradient(cfg.cx, cfg.cy, 10, cfg.cx, cfg.cy, cfg.w * 0.72);
  bg.addColorStop(0, "#252040");
  bg.addColorStop(1, "#0e0c1c");
  c.fillStyle = bg;
  c.fillRect(0, 0, cfg.w, cfg.h);
  SP.drawPlatform(c, cfg.cx, cfg.cy, cfg.platformR, t);
  for (const z of zones) SP.drawZone(c, z, t);
  if (wall) SP.drawWall(c, wall, t);
  for (const b of beams) SP.drawBeam(c, b, t);
  SP.drawShockwaves(c, sim.shockwaves);
  const bob = Math.sin(t * 2.6) * 1.1;
  if (A.invis) {
    c.save(); c.globalAlpha = A.invis; SP.drawWizard(c, casterX, cfg.y, cfg.casterRobe, 1, bob, cfg.playerR, castGlow); c.restore();
  } else {
    SP.drawWizard(c, casterX, cfg.y, cfg.casterRobe, 1, bob, cfg.playerR, castGlow);
  }
  const tx = targetX + targetKnock;
  SP.drawWizard(c, tx, cfg.y, cfg.targetRobe, -1, bob * 0.9, cfg.playerR, 0);
  if (targetFlash > 0) {
    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = targetFlash * 0.55;
    c.fillStyle = col;
    c.beginPath(); c.arc(tx, cfg.y, cfg.playerR * 1.5, 0, TAU); c.fill();
    c.restore();
  }
  SP.drawParticles(c, sim.particles);
  for (const p of projs) {
    c.save();
    c.translate(p.x, p.y);
    const pid = p.projSpell || spellId;
    const pcol = p.col || col;
    const rr = p.pr || (p.big ? pr * 1.45 : pr);
    SP.drawProj(c, pid, rr, pcol, p.ang || 0, t);
    c.restore();
  }
  if (A.ghostX != null) SP.drawWizard(c, A.ghostX, cfg.y, cfg.casterRobe, 1, 0, cfg.playerR, 0.35);
  if (s.type === "illusion" && lt >= flyStart) {
    for (let i = 0; i < 2; i++) {
      SP.drawDecoy(c, casterX + (i - 0.5) * 24, cfg.y, cfg.casterRobe, t, 0.5);
    }
  }
}

SP.makeScene = function(spellId) {
  const scene = {
    ...SP.PREVIEW_CFG,
    spellId,
    loop: SP.loopFor(spellId),
    draw(sim, t, dt) { SP.draw(sim, t, dt); },
  };
  if (spellId === "reflect") scene.loop = 3.3;
  if (spellId === "voidrip") {
    scene.loop = 3.4;
    scene.hitX = scene.cx;
  }
  return scene;
}
